/* eslint-disable no-shadow */
/* eslint-disable react/jsx-indent */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import useDynamicRefs from 'use-dynamic-refs';

import { DEVICE_TYPE } from '@/services/Constants';

import Foldout from '@/components/dropdown/components/foldout/FoldoutOptions';
import FormDropdown from '@/components/dropdown/components/FormDropdown';
import MultiDropdown from '@/components/dropdown/components/MultiDropdown';
import SimpleDropdown from '@/components/dropdown/components/SimpleDropdown';
import { DropdownOption, DropdownWrapperProps } from '@/components/dropdown/types';
import SearchInput from '@/components/input/SearchInput';
import ScrollBar from '@/components/ScrollBar';
import Spinner from '@/components/spinner/Spinner';

const DropdownWrapper = ({
	additionalElements,
	children,
	className,
	clearAll = false,
	closeOnSelectItem = true,
	color,
	customHeader,
	optionsHeader,
	disabled = false,
	disableClickOutside = false,
	drawerTitle,
	filter = false,
	fitContent,
	floatingLabel,
	handleDd,
	hideExpandedIcon = false,
	isActive = false,
	label,
	loading,
	menuHeight = '100vh',
	menuPosition = 'right',
	name: dataNameAttribute = '',
	nested = false,
	nestedHeader,
	onActive,
	onChange = () => {},
	onClearAll,
	onlyAction,
	optionAdditionalSubValueKey,
	optionAvaKey,
	optionAvaPosition,
	optionCheckedKey = 'checked',
	optionContinuationKey = '',
	optionDescriptionKey = 'description',
	optionFlagKey = 'flag',
	optionFlagProperty = 'flag',
	optionForDisabled = 'disabled',
	optionIconKey = 'icon',
	optionImageKey = 'image',
	optionLabelKey = 'label',
	optionAdditionalLabelKey = 'additional_label',
	optionSectionBodyKey = 'body',
	optionSectionKey = 'section',
	optionSubValueKey,
	optionTitleKey = 'title',
	optionTypeKey = 'type',
	optionValueKey = 'key',
	optionButtonType = 'link-border',
	optionClassName = '',
	options: initOptions = [],
	placeholder = '',
	searchFocusOnMount = false,
	searchPlaceholder = '',
	showHeaderIcon,
	showPlaceholderIcon,
	skeletonOptions = 0,
	titleInDropdown = '',
	type,
	value,
	valuePrefix,
	withSearch = false,
	withSelectedStyles = false,
	positioned,
	positionedContainer,
	positionOnTop = false,
	showDrawer = false,
	coverFull = false,
	testId,
}: DropdownWrapperProps) => {
	const isMobileResolution = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
	const isMobileView = isMobileResolution || !!showDrawer;
	const node = useRef<HTMLDivElement | null>(null);
	const [getRef, setRef] = useDynamicRefs();
	const dropDownMenu = useRef<HTMLDivElement | null>(null);
	const [listVisible, setListVisible] = useState<boolean>(false);
	const html = typeof document !== 'undefined' ? document.getElementsByTagName('html')[0] : null;
	const [selectValue, setSelectValue] = useState<any>(
		type === 'multi' ? value || [] : value || null,
	);
	const [searchValue, setSearchValue] = useState<string>('');
	const [options, setOptions] = useState<any[]>(initOptions as any[]);
	const [positionedTop, setPositionedTop] = useState<boolean>(false);
	const [autoFocusOnSearch, setAutoFocusOnSearch] = useState<boolean>(false);

	useEffect(() => {
		setListVisible(!!isActive);
	}, [isActive]);

	useEffect(() => {
		setSelectValue(type === 'multi' ? value || [] : value || null);
	}, [value]); // keep state and props in sync

	useEffect(() => {
		if (listVisible && isMobileView && html?.classList) html.classList.add('hide-scroll');
		return () => {
			if (html?.classList?.contains('hide-scroll')) {
				const removeClass = () => html.classList.remove('hide-scroll');
				window.setTimeout(removeClass, 200);
			}
		};
	}, [listVisible]);

	useEffect(() => {
		const handler = (e: any) => handleClickOutside(e);
		document.addEventListener('mousedown', handler);
		return () => {
			document.removeEventListener('mousedown', handler);
		};
	}, [listVisible]);

	useEffect(() => {
		listVisible &&
			searchFocusOnMount &&
			!DEVICE_TYPE.touch &&
			setAutoFocusOnSearch(!autoFocusOnSearch);
		onActive && onActive(listVisible);
		!listVisible && setSearchValue('');
		if (handleDd) isMobileView ? handleDd(!listVisible) : handleDd(true);
	}, [listVisible]);

	useEffect(() => {
		if ((initOptions as any[]).length) {
			setOptions(initOptions as any[]);
		}
	}, [initOptions]);

	useEffect(() => {
		if (searchValue.trim().length) {
			if (nested) {
				const copiedOptions = [...(initOptions as any[])];
				const filteredData: any[] = [];
				copiedOptions.forEach((item: any) => {
					const nestedResult = item[optionSectionBodyKey].filter((nestedItem: any) =>
						(nestedItem[optionTitleKey] as string)
							.toLowerCase()
							.includes(searchValue.trim().toLowerCase()),
					);

					if (nestedResult.length) {
						filteredData.push({
							[optionValueKey]: item[optionValueKey],
							[optionSectionKey]: item[optionSectionKey],
							[optionSectionBodyKey]: nestedResult,
						});
					}
				});
				setOptions(filteredData);
			} else {
				const copiedOptions = [...(initOptions as any[])];
				const filteredData = copiedOptions.filter((item: any) =>
					(item[optionTitleKey] as string)
						.toLowerCase()
						.includes(searchValue.trim().toLowerCase()),
				);
				setOptions(filteredData);
			}
		} else {
			setOptions(initOptions as any[]);
		}
	}, [searchValue]);

	useEffect(() => {
		if (listVisible) {
			const drawerAny = getRef(`drawer${listVisible && 'Active'}`) as any;
			const drawerRef = drawerAny && drawerAny.current;
			if (!drawerRef) return;
			const drawerRect = drawerRef.getBoundingClientRect();

			const drawerRightEdge = drawerRect.right;
			const drawerLeftEdge = drawerRect.left;
			const viewportWidth = window.innerWidth;

			if (drawerRightEdge > viewportWidth) {
				drawerRef.style.left = `-${drawerRightEdge - viewportWidth + 16}px`;
			} else if (drawerLeftEdge < 0) {
				const leftOffset = Math.abs(drawerLeftEdge) + 16;
				drawerRef.style.left = `${leftOffset}px`;
			}
		}
	}, [listVisible]);

	useEffect(() => {
		if (positioned && positionedContainer && listVisible) {
			const parentEl = (positionedContainer as any).current || positionedContainer;
			const parentElRect = parentEl.getBoundingClientRect();
			const drawerAny = getRef(`drawer${listVisible && 'Active'}`) as any;
			const drawerEl = drawerAny && drawerAny.current;
			const drawerRect = drawerEl.getBoundingClientRect();

			setPositionedTop(
				parentElRect.bottom < drawerRect.bottom &&
					drawerRect.top - drawerRect.height - 40 > parentElRect.top,
			);
		} else {
			setPositionedTop(false);
		}
	}, [listVisible]);

	const handleClickOutside = (e: any) => {
		const drawer = getRef('drawerActive');

		if (
			disableClickOutside ||
			(node.current && node.current.contains(e.target)) ||
			(drawer && (drawer as any).current && (drawer as any).current.contains(e.target))
		) {
			return;
		}
		listVisible && setListVisible(false);
	};

	const toggleDropdown = (e?: any) => {
		if (e) {
			e.preventDefault?.();
			e.stopPropagation?.();
		}
		setListVisible(!listVisible);
	};

	// eslint-disable-next-line no-shadow
	const handleItem = (item: DropdownOption, label?: any, checked?: boolean) => {
		if (type === 'multi') {
			const newVal = checked
				? (selectValue as any[]).filter((el) => el[optionValueKey] !== item[optionValueKey])
				: ([...(selectValue as any[]), ...[item]] as any[]);
			setSelectValue(newVal);
			onChange && onChange(newVal);
			return;
		}
		!onlyAction && setSelectValue(item);
		onChange && onChange(item, label);
		closeOnSelectItem && setListVisible(false);
	};

	const optionsMenuHeight = isMobileView ? '80vh' : (menuHeight as any);
	const foldout = (
		<div
			className={classNames('dropdown-options-wrap', {
				'active ': listVisible,
				'fit-content': !!fitContent,
				'nested-content': !!nested,
				'position-left': menuPosition === 'left',
				'position-right': menuPosition === 'right',
			})}
		>
			<div
				ref={setRef(`drawer${listVisible && 'Active'}`) as any}
				className={classNames('dropdown-options', {
					'active ': listVisible,
					'fit-content': !!fitContent,
					'nested-content': !!nested,
					'position-left': menuPosition === 'left',
					'position-right': menuPosition === 'right',
					'position-top': positionedTop || positionOnTop,
				})}
			>
				{loading && (
					<div className="loading-wrapper">
						<Spinner />
					</div>
				)}
				{optionsHeader}
				{drawerTitle && (
					<div className="header">
						<h4 className="header-title">{drawerTitle}</h4>
						<button className="btn-icon-link" type="button" onClick={toggleDropdown}>
							<span className="icon icon-close" />
						</button>
					</div>
				)}
				{withSearch &&
					((
						<SearchInput
							hideBackBtn
							searchBar={false}
							onChange={
								((val: string) => {
									setSearchValue(val);
								}) as any
							}
							value={searchValue}
							placeholder={searchPlaceholder}
							autoFocus={searchFocusOnMount ? autoFocusOnSearch : undefined}
						/>
					) as any)}
				<ScrollBar autoHeightMax={optionsMenuHeight} isMobileView={isMobileView}>
					<div ref={dropDownMenu} className="body" data-testid="body">
						{titleInDropdown && (
							<span className="text section-title">{titleInDropdown}</span>
						)}
						{nestedHeader && children}
						{(!nestedHeader && children) || (
							<Foldout
								additionalElements={additionalElements}
								handleItem={handleItem}
								listVisible={listVisible}
								nested={nested}
								optionAdditionalSubValueKey={optionAdditionalSubValueKey}
								optionAvaKey={optionAvaKey}
								optionAvaPosition={optionAvaPosition}
								optionCheckedKey={optionCheckedKey as any}
								optionContinuationKey={optionContinuationKey}
								optionDescriptionKey={optionDescriptionKey}
								optionFlagKey={optionFlagKey}
								optionFlagProperty={optionFlagProperty}
								optionForDisabled={optionForDisabled as any}
								optionIconKey={optionIconKey}
								optionImageKey={optionImageKey}
								optionLabelKey={optionLabelKey}
								optionAdditionalLabelKey={optionAdditionalLabelKey}
								optionSectionBodyKey={optionSectionBodyKey}
								optionSectionKey={optionSectionKey}
								optionSubValueKey={optionSubValueKey}
								optionTitleKey={optionTitleKey}
								optionTypeKey={optionTypeKey}
								optionValueKey={optionValueKey}
								optionButtonType={optionButtonType}
								optionClassName={optionClassName}
								options={options}
								selectValue={selectValue}
								type={type}
								skeletonOptions={skeletonOptions}
							/>
						)}
					</div>
				</ScrollBar>
			</div>
		</div>
	);

	return (
		<div
			ref={node}
			className={classNames('dropdown', { disabled: hideExpandedIcon }, className, {
				'display-drawer': showDrawer && isMobileView,
			})}
			data-testid={testId}
		>
			{customHeader && (
				<div
					className={classNames(
						'custom-header',
						{ active: listVisible },
						{ expanded: listVisible },
					)}
					onClick={!disabled ? toggleDropdown : () => {}}
				>
					{customHeader}
					<span className="dropdown-arrow" />
				</div>
			)}
			{type === 'simple' && (
				<SimpleDropdown
					color={color}
					expanded={listVisible}
					toggleDropdown={!disabled ? toggleDropdown : () => {}}
					headerIcon={selectValue && (selectValue as any)[optionIconKey as any]}
					selectedValue={selectValue && (selectValue as any)[optionTitleKey as any]}
					placeholder={placeholder}
					filter={filter}
					dataNameAttribute={dataNameAttribute as any}
					showHeaderIcon
					withSelectedStyles={withSelectedStyles}
					disabled={disabled}
					clearAll={clearAll}
					onClearAll={onClearAll}
					coverFull={coverFull}
				/>
			)}

			{type === 'form' && (
				<FormDropdown
					expanded={listVisible}
					toggleDropdown={!disabled ? toggleDropdown : () => {}}
					headerIcon={selectValue && (selectValue as any)[optionIconKey as any]}
					headerAva={selectValue && (selectValue as any)[optionAvaKey as any]}
					headerAvaOutside={selectValue && (selectValue as any)[optionAvaPosition as any]}
					headerFlag={selectValue && (selectValue as any)[optionFlagKey as any]}
					selectedValue={selectValue && (selectValue as any)[optionTitleKey as any]}
					selectedSubValue={selectValue && (selectValue as any)[optionSubValueKey as any]}
					showHeaderIcon={showHeaderIcon}
					showPlaceholderIcon={showPlaceholderIcon}
					showHeaderAva
					floatingLabel={!!floatingLabel}
					color={color as any}
					label={label || placeholder}
					dataNameAttribute={dataNameAttribute as any}
					hideExpandedIcon={!!hideExpandedIcon}
					valuePrefix={valuePrefix}
					optionFlagProperty={optionFlagProperty}
					loading={loading}
				/>
			)}

			{type === 'multi' && (
				<MultiDropdown
					expanded={listVisible}
					toggleDropdown={!disabled ? toggleDropdown : () => {}}
					selectedValue={selectValue}
					color={color as any}
					label={(label || placeholder) as any}
					dataNameAttribute={dataNameAttribute as any}
					hideExpandedIcon={!!hideExpandedIcon}
					handleItem={handleItem}
					optionTitleKey={optionTitleKey}
				/>
			)}

			{showDrawer && isMobileView
				? createPortal(
						<div
							className={classNames('dropDown-portal', className, {
								active: listVisible,
							})}
						>
							{foldout}
						</div>,
						document.getElementById('root') as any,
				  )
				: foldout}
		</div>
	);
};

export default DropdownWrapper;
